/**
 * User Photo Cache Manager
 * Fetches user photos from Microsoft Graph API and caches them in localStorage
 */

const CACHE_PREFIX = 'userPhoto_';
const CACHE_EXPIRY_DAYS = 7;

/**
 * Get user photo by email address
 * @param {string} userEmail - The user's email address
 * @returns {Promise<string|null>} - Base64 encoded photo or null if not available
 */
export async function getUserPhoto(userEmail) {
    if (!userEmail) {
        console.warn('[UserPhotoCache] No email provided');
        return null;
    }

    // Check cache first
    const cacheKey = CACHE_PREFIX + userEmail;
    const cachedData = localStorage.getItem(cacheKey);

    if (cachedData) {
        try {
            const parsed = JSON.parse(cachedData);
            const cacheAge = Date.now() - parsed.timestamp;
            const maxAge = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

            if (cacheAge < maxAge) {
                console.log('[UserPhotoCache] Using cached photo for:', userEmail);
                return parsed.photo;
            } else {
                console.log('[UserPhotoCache] Cache expired for:', userEmail);
            }
        } catch (e) {
            console.error('[UserPhotoCache] Failed to parse cached data:', e);
        }
    }

    // Fetch from Microsoft Graph
    return await fetchAndCacheUserPhoto(userEmail);
}

/**
 * Fetch user photo from Microsoft Graph and cache it
 * @param {string} userEmail - The user's email address
 * @returns {Promise<string|null>} - Base64 encoded photo or null if not available
 */
async function fetchAndCacheUserPhoto(userEmail) {
    try {
        const accessToken = localStorage.getItem('accessToken-graph');

        if (!accessToken) {
            console.warn('[UserPhotoCache] No Graph API access token available');
            return null;
        }

        console.log('[UserPhotoCache] Fetching photo for:', userEmail);

        // Use Microsoft Graph to fetch user photo by email
        const response = await fetch(`https://graph.microsoft.com/v1.0/users/${userEmail}/photo/$value`, {
            headers: {
                'Authorization': 'Bearer ' + accessToken
            }
        });

        if (response.status === 200) {
            const blob = await response.blob();

            // Convert to base64
            const base64Photo = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });

            // Cache the photo
            const cacheKey = CACHE_PREFIX + userEmail;
            const cacheData = {
                photo: base64Photo,
                timestamp: Date.now()
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));

            console.log('[UserPhotoCache] Successfully fetched and cached photo for:', userEmail);
            return base64Photo;
        } else if (response.status === 404) {
            console.log('[UserPhotoCache] User has no photo:', userEmail);
            // Cache the "no photo" result to avoid repeated requests
            const cacheKey = CACHE_PREFIX + userEmail;
            const cacheData = {
                photo: null,
                timestamp: Date.now()
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            return null;
        } else {
            console.warn('[UserPhotoCache] Failed to fetch photo:', response.status, response.statusText);
            return null;
        }
    } catch (error) {
        console.error('[UserPhotoCache] Error fetching user photo:', error);
        return null;
    }
}

/**
 * Clear all cached user photos
 */
export function clearUserPhotoCache() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
            localStorage.removeItem(key);
        }
    });
    console.log('[UserPhotoCache] Cleared all cached photos');
}
